import { Collapse, Stack, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { GuidelineBlockDTO, GuidelineDTO, productKindFromNotionDisplayName } from 'models/guidelines'
import { useGetCreativeInstructions, useGetGuidelinePageByProductKind, useListGuidelines } from 'dataQueries'

import { BLUE_900, CORAL_700, GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GuidelinesRenderer } from '../GuidelinesRenderer'
import { InstructionsBanner } from '../InstructionsBanner'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { LoadingWrapper } from 'components/common/LoadingWrapper'
import NotFound from 'components/pages/General/NotFound/NotFound'
import { PageContent } from 'components/common/Page/PageContent'
import { ProductKind } from 'constants/product'
import { logger } from 'utils/logging'
import styles from './CreativeGuidelines.module.sass'
import { useAuth0 } from 'utils/auth'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

const PRODUCT_KIND_VALUES = new Set<string>(Object.values(ProductKind))

const readProductKindParam = (raw: string | null): ProductKind | undefined => {
  if (!raw) return undefined
  return PRODUCT_KIND_VALUES.has(raw) ? (raw as ProductKind) : undefined
}

interface Props {
  productKindFromPath?: string
}

export const CreativeGuidelinesController: FC<Props> = ({ productKindFromPath }) => {
  const { t } = useTranslation(['creative_guidelines'])
  const { roles } = useAuth0()
  const { baseUserData } = useUserData()
  const allowPage = useFeatureFlag(FeatureFlag.ALLOW_CT_GUIDELINES_PAGE)
  // Defense in depth: the role check below renders NotFound, but the queries would still fire
  // before that — gate their enabled predicates on the role too.
  const canView = allowPage && (roles.isCreative || roles.isAdmin)

  const [searchParams] = useSearchParams()
  const creativeInstructionsPairingId = searchParams.get('creativeInstructionsPairingId') ?? undefined
  // BE falls back to English when guidelines aren't translated to the requested language, so we always send the CT's stored language. URL `?language=` overrides for admin preview.
  const language = searchParams.get('language') ?? baseUserData?.language ?? undefined

  const { data: listData, isLoading: listLoading, isError: listError } =
    useListGuidelines(language, canView)

  const guidelinesList = listData?.data

  const preselectedKind = readProductKindParam(productKindFromPath ?? null)
  const [previewProductKind, setPreviewProductKind] = useState<ProductKind | undefined>(() => preselectedKind)

  const { data: creativeInstructionsResponse, isError: creativeInstructionsError } =
    useGetCreativeInstructions(creativeInstructionsPairingId, canView && !!creativeInstructionsPairingId)
  const creativeInstructions = creativeInstructionsResponse?.data

  // An unrecognized :productKind URL param is silently ignored (the effect below then
  // auto-selects the first guideline with no mismatch signal) — surface it to Sentry so
  // stale or misgenerated deep links are visible. Fires per mount/param change, not per render.
  useEffect(() => {
    if (productKindFromPath && !readProductKindParam(productKindFromPath)) {
      logger.warn('Unrecognized :productKind URL param on the guidelines page', undefined, { context: { component: 'CreativeGuidelinesController', action: 'readProductKindParam', metadata: { rawParam: productKindFromPath } } })
    }
  }, [productKindFromPath])

  useEffect(() => {
    if (previewProductKind || !guidelinesList?.length) return

    const firstSelectable = guidelinesList.find(item => !!productKindFromNotionDisplayName(item.productId))
    const firstKind = firstSelectable ? productKindFromNotionDisplayName(firstSelectable.productId) : undefined

    if (firstKind) setPreviewProductKind(firstKind)
  }, [previewProductKind, guidelinesList])

  // Surface Notion 'Product' values that have no ProductKind mapping (map drift vs the BE
  // GuidelinesProductKindMapper) — these render as disabled, unclickable list buttons with
  // no other signal, so log them once per list load.
  useEffect(() => {
    if (!guidelinesList?.length) return

    const unmapped = guidelinesList
      .filter(item => item.productId && !productKindFromNotionDisplayName(item.productId))
      .map(item => item.productId)

    if (unmapped.length) {
      logger.warn('Notion guideline product(s) have no ProductKind mapping (map drift)', { unmappedProductIds: unmapped }, { context: { component: 'CreativeGuidelinesController' } })
    }
  }, [guidelinesList])

  const { data: guidelineData, isLoading: guidelineLoading, isError: guidelineError } =
    useGetGuidelinePageByProductKind(previewProductKind, language, canView)

  const guidelinePage = guidelineData?.data

  if (!allowPage) return <NotFound />
  if (!roles.isCreative && !roles.isAdmin) return <NotFound />

  return (
    <PageContent>
      <Stack gap="5.6rem" padding="5.6rem 0">

        <Typography variant="text-2xl" fontWeight={600} color={GRAY_900} sx={{ mt: '4rem' }}>
          {t(roles.isAdmin && !roles.isCreative ? 'title_admin' : 'title')}
        </Typography>

        {creativeInstructions &&
          <InstructionsBanner data={creativeInstructions} />
        }

        <Section title={t('product_guidelines_heading')}>

          <LoadingWrapper isLoading={listLoading}>

            {(listError || creativeInstructionsError) && <p className={styles.error}>{t('list_error')}</p>}

            {guidelinesList && guidelinesList.length === 0 &&
              <p className={styles.notionPlaceholder}>{t('list_empty')}</p>
            }

            {guidelinesList && guidelinesList.length > 0 &&
              <GuidelinesList
                items={guidelinesList}
                selected={previewProductKind}
                preselectedKind={preselectedKind}
                onSelect={setPreviewProductKind}
              />
            }

          </LoadingWrapper>

          {previewProductKind &&
            <GuidelinePageBody
              isLoading={guidelineLoading}
              isError={guidelineError}
              blocks={guidelinePage?.blocks}
              errorLabel={t('load_error')}
              emptyLabel={t('product_guidelines_pending')}
            />
          }

        </Section>

      </Stack>
    </PageContent>
  )
}

const Section: FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
  <Stack gap="1.6rem">

    <Typography variant="text-xl" fontWeight={600} color={GRAY_900}>
      {title}
    </Typography>

    <BorderBoxWrapper padding={2.4} gap={1.6} display="flex" flexDirection="column">
      {children}
    </BorderBoxWrapper>

  </Stack>
)

interface GuidelinePageBodyProps {
  isLoading: boolean
  isError: boolean
  blocks: GuidelineBlockDTO[] | undefined
  errorLabel: string
  emptyLabel: string
}

const GuidelinePageBody: FC<GuidelinePageBodyProps> = ({ isLoading, isError, blocks, errorLabel, emptyLabel }) => (
  <LoadingWrapper isLoading={isLoading}>

    {isError && <p className={styles.error}>{errorLabel}</p>}

    {!isError && blocks && blocks.length === 0 &&
      <p className={styles.notionPlaceholder}>{emptyLabel}</p>
    }

    {!isError && blocks && blocks.length > 0 && <GuidelinesRenderer blocks={blocks} />}

  </LoadingWrapper>
)

interface GuidelinesListProps {
  items: GuidelineDTO[]
  selected: ProductKind | undefined
  preselectedKind: ProductKind | undefined
  onSelect: (productKind: ProductKind) => void
}

const GuidelinesList: FC<GuidelinesListProps> = ({ items, selected, preselectedKind, onSelect }) => {
  const { t } = useTranslation(['creative_guidelines'])

  const preselectedItem = preselectedKind
    ? items.find(item => productKindFromNotionDisplayName(item.productId) === preselectedKind)
    : undefined

  const isMismatched = !!preselectedItem && selected !== preselectedKind
  const [isExpanded, setIsExpanded] = useState(isMismatched)

  if (!preselectedItem) {
    return (
      <ul className={styles.guidelinesList}>
        {items.map(item => (
          <li key={item.id}>
            <GuidelineButton item={item} selected={selected} onSelect={onSelect} />
          </li>
        ))}
      </ul>
    )
  }

  const otherItems = items.filter(item => productKindFromNotionDisplayName(item.productId) !== preselectedKind)

  return (
    <Stack gap="0.8rem">

      <div className={styles.guidelinesRow}>
        <GuidelineButton
          item={preselectedItem}
          selected={selected}
          onSelect={onSelect}
          suffix={t('assignment_guideline_suffix')}
        />
      </div>

      <button
        type="button"
        className={styles.preselectedToggle}
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        style={isMismatched ? { marginBottom: '2rem' } : undefined}
      >
        <Typography variant="text-sm" fontWeight={700} fontStyle="italic" color={isMismatched ? CORAL_700 : BLUE_900}>
          {t(isMismatched ? 'mismatched_guideline_warning' : 'preselected_explanation')}
        </Typography>
        <KeyboardArrowDownRoundedIcon
          className={isExpanded ? styles.preselectedToggleIconOpen : styles.preselectedToggleIcon}
        />
      </button>

      <Collapse in={isExpanded}>
        <div className={styles.guidelinesRow}>
          {otherItems.map(item => (
            <GuidelineButton key={item.id} item={item} selected={selected} onSelect={onSelect} />
          ))}
        </div>
      </Collapse>

    </Stack>
  )
}

interface GuidelineButtonProps {
  item: GuidelineDTO
  selected: ProductKind | undefined
  onSelect: (productKind: ProductKind) => void
  suffix?: string
}

const GuidelineButton: FC<GuidelineButtonProps> = ({ item, selected, onSelect, suffix }) => {
  const candidateKind = productKindFromNotionDisplayName(item.productId)
  const disabled = !candidateKind
  const isSelected = !!candidateKind && candidateKind === selected

  return (
    <button
      type="button"
      className={isSelected ? styles.guidelineItemActive : styles.guidelineItem}
      onClick={() => candidateKind && onSelect(candidateKind)}
      disabled={disabled}
    >
      {suffix ? `${item.title} ${suffix}` : item.title}
    </button>
  )
}
